
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Search, Navigation } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { geocodeAddressCached } from '@/utils/osmGeocoding';
import { isLocationInAllowedZone } from '@/utils/zoneValidation';
import { useToast } from '@/hooks/use-toast';

interface LocationSuggestion {
  id: string;
  name: string;
  address: string;
  city: string;
  region: string;
  lat: number;
  lng: number;
}

interface PreciseLocationSearchProps {
  onLocationSelect: (location: { address: string; city: string; lat: number; lng: number }) => void;
  placeholder?: string;
  value?: string;
  showCurrentLocationButton?: boolean;
}

// COMPREHENSIVE Gambian locations with coordinates - ALL major locations
const gambianLocations: LocationSuggestion[] = [
  // Greater Banjul Area with precise coordinates
  { id: '1', name: 'Banjul', address: 'Banjul City Center', city: 'Banjul', region: 'Banjul', lat: 13.4549, lng: -16.5790 },
  { id: '2', name: 'Bakau', address: 'Bakau New Town', city: 'Bakau', region: 'Western', lat: 13.4785, lng: -16.6818 },
  { id: '3', name: 'Fajara', address: 'Fajara Golf Course Area', city: 'Fajara', region: 'Western', lat: 13.4966, lng: -16.6729 },
  { id: '4', name: 'Kairaba', address: 'Kairaba Avenue', city: 'Kairaba', region: 'Western', lat: 13.4662, lng: -16.6594 },
  { id: '5', name: 'Pipeline', address: 'Pipeline Area', city: 'Pipeline', region: 'Western', lat: 13.4617, lng: -16.6447 },
  { id: '6', name: 'Kololi', address: 'Kololi Beach Resort Area', city: 'Kololi', region: 'Western', lat: 13.4731, lng: -16.7214 },
  { id: '7', name: 'Bijilo', address: 'Bijilo Forest Park Area', city: 'Bijilo', region: 'Western', lat: 13.4678, lng: -16.7156 },
  { id: '8', name: 'Brufut', address: 'Brufut Heights', city: 'Brufut', region: 'Western', lat: 13.4012, lng: -16.7389 },
  { id: '9', name: 'Sukuta', address: 'Sukuta Traffic Light', city: 'Sukuta', region: 'Western', lat: 13.4167, lng: -16.7092 },
  { id: '10', name: 'Brusubi', address: 'Brusubi Phase 1', city: 'Brusubi', region: 'Western', lat: 13.4289, lng: -16.7178 },
  { id: '11', name: 'Brusubi Phase 2', address: 'Brusubi Phase 2', city: 'Brusubi Phase 2', region: 'Western', lat: 13.4295, lng: -16.7185 },
  { id: '12', name: 'Brusubi Phase 3', address: 'Brusubi Phase 3', city: 'Brusubi Phase 3', region: 'Western', lat: 13.4301, lng: -16.7192 },
  { id: '13', name: 'Brusubi Phase 4', address: 'Brusubi Phase 4', city: 'Brusubi Phase 4', region: 'Western', lat: 13.4307, lng: -16.7199 },
  
  // Serrekunda and surroundings
  { id: '14', name: 'Serrekunda', address: 'Serrekunda Market Area', city: 'Serrekunda', region: 'Western', lat: 13.4434, lng: -16.6775 },
  { id: '15', name: 'Latrikunda', address: 'Latrikunda Sabiji', city: 'Latrikunda', region: 'Western', lat: 13.4423, lng: -16.6834 },
  { id: '16', name: 'Latrikunda German', address: 'Latrikunda German', city: 'Latrikunda German', region: 'Western', lat: 13.4430, lng: -16.6840 },
  { id: '17', name: 'Latrikunda Yiringanya', address: 'Latrikunda Yiringanya', city: 'Latrikunda Yiringanya', region: 'Western', lat: 13.4437, lng: -16.6847 },
  { id: '18', name: 'Tallinding', address: 'Tallinding Kunjang', city: 'Tallinding', region: 'Western', lat: 13.4512, lng: -16.6912 },
  { id: '19', name: 'Dippa Kunda', address: 'Dippa Kunda', city: 'Dippa Kunda', region: 'Western', lat: 13.4389, lng: -16.6723 },
  { id: '20', name: 'New Jeshwang', address: 'New Jeshwang', city: 'New Jeshwang', region: 'Western', lat: 13.4400, lng: -16.6650 },
  { id: '21', name: 'Old Jeshwang', address: 'Old Jeshwang', city: 'Old Jeshwang', region: 'Western', lat: 13.4395, lng: -16.6645 },
  { id: '22', name: 'Westfield', address: 'Westfield Junction', city: 'Westfield', region: 'Western', lat: 13.4550, lng: -16.6700 },
  { id: '23', name: 'London Corner', address: 'London Corner', city: 'Serrekunda', region: 'Western', lat: 13.4445, lng: -16.6780 },
  
  // Kombo areas
  { id: '24', name: 'Brikama', address: 'Brikama Ba', city: 'Brikama', region: 'Western', lat: 13.2711, lng: -16.6500 },
  { id: '25', name: 'Brikama Nding', address: 'Brikama Nding', city: 'Brikama Nding', region: 'Western', lat: 13.2720, lng: -16.6510 },
  { id: '26', name: 'Brikama Kabafita', address: 'Brikama Kabafita', city: 'Brikama Kabafita', region: 'Western', lat: 13.2730, lng: -16.6520 },
  { id: '27', name: 'Gunjur', address: 'Gunjur Village', city: 'Gunjur', region: 'Western', lat: 13.2017, lng: -16.7333 },
  { id: '28', name: 'Sanyang', address: 'Sanyang Beach', city: 'Sanyang', region: 'Western', lat: 13.2333, lng: -16.7500 },
  { id: '29', name: 'Tanji', address: 'Tanji Fishing Village', city: 'Tanji', region: 'Western', lat: 13.2833, lng: -16.7667 },
  { id: '30', name: 'Kartong', address: 'Kartong Beach', city: 'Kartong', region: 'Western', lat: 13.0500, lng: -16.7500 },
  { id: '31', name: 'Tujereng', address: 'Tujereng Village', city: 'Tujereng', region: 'Western', lat: 13.1833, lng: -16.7000 },
  { id: '32', name: 'Batokunku', address: 'Batokunku', city: 'Batokunku', region: 'Western', lat: 13.1500, lng: -16.7200 },
  { id: '33', name: 'Ghana Town', address: 'Ghana Town', city: 'Ghana Town', region: 'Western', lat: 13.2100, lng: -16.7100 },
  
  // Kanifing Municipality
  { id: '34', name: 'Kanifing', address: 'Kanifing Municipality', city: 'Kanifing', region: 'Western', lat: 13.4500, lng: -16.6833 },
  { id: '35', name: 'Bundung', address: 'Bundung', city: 'Bundung', region: 'Western', lat: 13.4333, lng: -16.6667 },
  { id: '36', name: 'Manjai', address: 'Manjai Kunda', city: 'Manjai', region: 'Western', lat: 13.4667, lng: -16.6500 },
  { id: '37', name: 'Bartez', address: 'Bartez', city: 'Bartez', region: 'Western', lat: 13.4600, lng: -16.6600 },
  { id: '38', name: 'Tabokoto', address: 'Tabokoto', city: 'Tabokoto', region: 'Western', lat: 13.4550, lng: -16.6550 },
  { id: '39', name: 'Churchills Town', address: 'Churchills Town', city: 'Churchills Town', region: 'Western', lat: 13.4300, lng: -16.6400 },
  { id: '40', name: 'Ebo Town', address: 'Ebo Town', city: 'Ebo Town', region: 'Western', lat: 13.4350, lng: -16.6450 },
  
  // Airport and surrounding areas
  { id: '41', name: 'Yundum', address: 'Yundum Barracks', city: 'Yundum', region: 'Western', lat: 13.3667, lng: -16.6833 },
  { id: '42', name: 'Banjul International Airport', address: 'Banjul International Airport Terminal', city: 'Yundum', region: 'Western', lat: 13.3380, lng: -16.6522 },
  { id: '43', name: 'Old Yundum', address: 'Old Yundum', city: 'Old Yundum', region: 'Western', lat: 13.3650, lng: -16.6820 },
  { id: '44', name: 'New Yundum', address: 'New Yundum', city: 'New Yundum', region: 'Western', lat: 13.3680, lng: -16.6850 },
  
  // Nature reserves and tourist areas
  { id: '45', name: 'Abuko', address: 'Abuko Nature Reserve', city: 'Abuko', region: 'Western', lat: 13.3967, lng: -16.6500 },
  { id: '46', name: 'Lamin', address: 'Lamin Village', city: 'Lamin', region: 'Western', lat: 13.3500, lng: -16.4333 },
  { id: '47', name: 'Sinchu Alagie', address: 'Sinchu Alagie', city: 'Sinchu Alagie', region: 'Western', lat: 13.3800, lng: -16.6200 },
  { id: '48', name: 'Kotu', address: 'Kotu Beach Resort Area', city: 'Kotu', region: 'Western', lat: 13.4800, lng: -16.7000 },
  { id: '49', name: 'Senegambia Strip', address: 'Senegambia Tourist Area', city: 'Kololi', region: 'Western', lat: 13.4740, lng: -16.7220 },
  
  // North Bank Region
  { id: '50', name: 'Barra', address: 'Barra Ferry Terminal', city: 'Barra', region: 'North Bank', lat: 13.4833, lng: -16.5500 },
  { id: '51', name: 'Essau', address: 'Essau Town', city: 'Essau', region: 'North Bank', lat: 13.5167, lng: -16.5333 },
  { id: '52', name: 'Farafenni', address: 'Farafenni Market', city: 'Farafenni', region: 'North Bank', lat: 13.5667, lng: -15.6000 },
  { id: '53', name: 'Kerewan', address: 'Kerewan Town', city: 'Kerewan', region: 'North Bank', lat: 13.4833, lng: -16.0833 },
  { id: '54', name: 'Amdalai', address: 'Amdalai Village', city: 'Amdalai', region: 'North Bank', lat: 13.5000, lng: -15.8000 },
  { id: '55', name: 'Albreda', address: 'Albreda Historical Site', city: 'Albreda', region: 'North Bank', lat: 13.3167, lng: -16.4500 },
  { id: '56', name: 'Juffureh', address: 'Juffureh Village', city: 'Juffureh', region: 'North Bank', lat: 13.3000, lng: -16.4000 },
  
  // Central River Region
  { id: '57', name: 'Georgetown', address: 'Georgetown (Janjanbureh)', city: 'Georgetown', region: 'Central River', lat: 13.5333, lng: -14.7667 },
  { id: '58', name: 'Kuntaur', address: 'Kuntaur Town', city: 'Kuntaur', region: 'Central River', lat: 13.6667, lng: -15.1333 },
  { id: '59', name: 'Bansang', address: 'Bansang Hospital Area', city: 'Bansang', region: 'Central River', lat: 13.4333, lng: -14.6500 },
  { id: '60', name: 'Wassu', address: 'Wassu Stone Circles', city: 'Wassu', region: 'Central River', lat: 13.6500, lng: -14.9000 },
  
  // Upper River Region
  { id: '61', name: 'Basse', address: 'Basse Santa Su', city: 'Basse', region: 'Upper River', lat: 13.3167, lng: -14.2167 },
  { id: '62', name: 'Fatoto', address: 'Fatoto Border', city: 'Fatoto', region: 'Upper River', lat: 13.4500, lng: -13.9500 },
  { id: '63', name: 'Gambisara', address: 'Gambisara Village', city: 'Gambisara', region: 'Upper River', lat: 13.4000, lng: -14.1000 },
  { id: '64', name: 'Koina', address: 'Koina Town', city: 'Koina', region: 'Upper River', lat: 13.2500, lng: -14.3000 },
  
  // Lower River Region
  { id: '65', name: 'Soma', address: 'Soma Town', city: 'Soma', region: 'Lower River', lat: 13.4333, lng: -15.5333 },
  { id: '66', name: 'Kaur', address: 'Kaur Town', city: 'Kaur', region: 'Lower River', lat: 13.4000, lng: -16.0833 },
  { id: '67', name: 'Kaiaf', address: 'Kaiaf Village', city: 'Kaiaf', region: 'Lower River', lat: 13.3000, lng: -15.7000 },
  { id: '68', name: 'Kiang West', address: 'Kiang West District', city: 'Kiang West', region: 'Lower River', lat: 13.3500, lng: -16.2000 },
  
  // Additional major locations with coordinates
  { id: '69', name: 'Busumbala', address: 'Busumbala Village', city: 'Busumbala', region: 'Western', lat: 13.3400, lng: -16.5600 },
  { id: '70', name: 'Darsilami', address: 'Darsilami Village', city: 'Darsilami', region: 'Western', lat: 13.3300, lng: -16.5500 },
  { id: '71', name: 'Wellingara', address: 'Wellingara Village', city: 'Wellingara', region: 'Western', lat: 13.2000, lng: -16.4200 },
  { id: '72', name: 'Kitty', address: 'Kitty Village', city: 'Kitty', region: 'Western', lat: 13.2900, lng: -16.5200 },
  { id: '73', name: 'Jabang', address: 'Jabang Village', city: 'Jabang', region: 'Western', lat: 13.3100, lng: -16.5400 },
  { id: '74', name: 'Sukuta Jabang', address: 'Sukuta Jabang', city: 'Sukuta Jabang', region: 'Western', lat: 13.4170, lng: -16.7100 },
  { id: '75', name: 'Berending', address: 'Berending Village', city: 'Berending', region: 'Western', lat: 13.3200, lng: -16.5800 },
  
  // Additional fishing villages and coastal areas
  { id: '76', name: 'Tanji Fishing Center', address: 'Tanji Fish Market', city: 'Tanji', region: 'Western', lat: 13.2840, lng: -16.7670 },
  { id: '77', name: 'Gunjur Beach', address: 'Gunjur Beach', city: 'Gunjur', region: 'Western', lat: 13.2020, lng: -16.7340 },
  { id: '78', name: 'Sanyang Beach', address: 'Sanyang Paradise Beach', city: 'Sanyang', region: 'Western', lat: 13.2340, lng: -16.7510 },
  { id: '79', name: 'Kartong Beach', address: 'Kartong Folonko Beach', city: 'Kartong', region: 'Western', lat: 13.0510, lng: -16.7510 },
  { id: '80', name: 'Brufut Beach Club', address: 'Brufut Beach Club Area', city: 'Brufut', region: 'Western', lat: 13.4020, lng: -16.7400 },
  
  // Major markets and commercial centers
  { id: '81', name: 'Serrekunda Market', address: 'Serrekunda Central Market', city: 'Serrekunda', region: 'Western', lat: 13.4440, lng: -16.6780 },
  { id: '82', name: 'Brikama Market', address: 'Brikama Main Market', city: 'Brikama', region: 'Western', lat: 13.2720, lng: -16.6510 },
  { id: '83', name: 'Albert Market', address: 'Albert Market Banjul', city: 'Banjul', region: 'Banjul', lat: 13.4560, lng: -16.5800 },
  
  // Educational institutions
  { id: '84', name: 'UTG Faraba Banta', address: 'University of The Gambia', city: 'Faraba Banta', region: 'Western', lat: 13.3100, lng: -16.4800 },
  { id: '85', name: 'Gambia College', address: 'Gambia College Brikama', city: 'Brikama', region: 'Western', lat: 13.2750, lng: -16.6550 },
  
  // Hospital areas
  { id: '86', name: 'RVTH', address: 'Royal Victoria Teaching Hospital', city: 'Banjul', region: 'Banjul', lat: 13.4570, lng: -16.5810 },
  { id: '87', name: 'Kanifing General Hospital', address: 'Kanifing General Hospital', city: 'Kanifing', region: 'Western', lat: 13.4510, lng: -16.6840 },
  { id: '88', name: 'Bansang Hospital', address: 'Bansang General Hospital', city: 'Bansang', region: 'Central River', lat: 13.4340, lng: -14.6510 },
  
  // Industrial and estate areas
  { id: '89', name: 'Kanifing Industrial Area', address: 'Kanifing Industrial Estate', city: 'Kanifing', region: 'Western', lat: 13.4520, lng: -16.6850 },
  { id: '90', name: 'Bijilo Hills', address: 'Bijilo Hills Estate', city: 'Bijilo', region: 'Western', lat: 13.4690, lng: -16.7170 },
  { id: '91', name: 'Kairaba Estate', address: 'Kairaba Estate', city: 'Kairaba', region: 'Western', lat: 13.4670, lng: -16.6600 },
  { id: '92', name: 'Fajara Club', address: 'Fajara Club Area', city: 'Fajara', region: 'Western', lat: 13.4970, lng: -16.6735 },
  { id: '93', name: 'Cape Point Estate', address: 'Cape Point Estate', city: 'Bakau', region: 'Western', lat: 13.4790, lng: -16.6825 },
  { id: '94', name: 'Brufut Gardens', address: 'Brufut Gardens', city: 'Brufut', region: 'Western', lat: 13.4030, lng: -16.7400 },
  
  // Additional villages and settlements
  { id: '95', name: 'Bambali', address: 'Bambali Village', city: 'Bambali', region: 'Western', lat: 13.3700, lng: -16.6100 },
  { id: '96', name: 'Mandinaba', address: 'Mandinaba Village', city: 'Mandinaba', region: 'Western', lat: 13.3600, lng: -16.6000 },
  { id: '97', name: 'Madina Salaam', address: 'Madina Salaam', city: 'Madina Salaam', region: 'Western', lat: 13.2800, lng: -16.5000 },
  { id: '98', name: 'Mandinary', address: 'Mandinary Village', city: 'Mandinary', region: 'Western', lat: 13.2600, lng: -16.4800 },
  { id: '99', name: 'Nyofelleh', address: 'Nyofelleh Village', city: 'Nyofelleh', region: 'Western', lat: 13.2400, lng: -16.4600 },
  { id: '100', name: 'Sanchaba', address: 'Sanchaba Village', city: 'Sanchaba', region: 'Western', lat: 13.2200, lng: -16.4400 },
];

const PreciseLocationSearch = ({ 
  onLocationSelect, 
  placeholder = "Enter any location (address, landmark, Google Plus code like C77W+5MW)...", 
  value = '',
  showCurrentLocationButton = true 
}: PreciseLocationSearchProps) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (query.length > 0) {
      const filtered = gambianLocations.filter(location =>
        location.name.toLowerCase().includes(query.toLowerCase()) ||
        location.address.toLowerCase().includes(query.toLowerCase()) ||
        location.city.toLowerCase().includes(query.toLowerCase()) ||
        location.region.toLowerCase().includes(query.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      // Show popular locations when no query
      setSuggestions(gambianLocations.slice(0, 20)); // Show first 20 by default
      setShowSuggestions(false);
    }
  }, [query]);

  const handleSuggestionClick = (suggestion: LocationSuggestion) => {
    setQuery(suggestion.address);
    setShowSuggestions(false);
    onLocationSelect({
      address: suggestion.address,
      city: suggestion.city,
      lat: suggestion.lat,
      lng: suggestion.lng
    });
  };

  const handleManualLocationSubmit = async () => {
    if (!query.trim()) {
      toast({
        title: "Invalid Location",
        description: "Please enter a location",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Try to geocode the entered location
      const geocoded = await geocodeAddressCached(query);
      
      if (!geocoded) {
        toast({
          title: "Location Not Found",
          description: "Unable to find this location. Please check the address or try a different format (like Google Plus codes: C77W+5MW).",
          variant: "destructive",
        });
        return;
      }

      // Validate if location is in allowed zone
      if (!isLocationInAllowedZone(geocoded.display_name)) {
        toast({
          title: "Location Not Supported",
          description: "Service is currently only available in Banjul and Western regions of The Gambia.",
          variant: "destructive",
        });
        return;
      }

      // Location is valid and in allowed zone
      onLocationSelect({
        address: geocoded.display_name,
        city: 'The Gambia', // Default since we validated it's in allowed zones
        lat: geocoded.latitude,
        lng: geocoded.longitude,
      });

      setShowSuggestions(false);
      toast({
        title: "Location Added",
        description: "Location successfully validated and added",
      });

    } catch (error) {
      console.error('Error geocoding location:', error);
      toast({
        title: "Error",
        description: "Failed to validate location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualLocationSubmit();
    }
  };

  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            
            console.log('Current position:', lat, lng);
            
            // Try to get address from coordinates using reverse geocoding
            let locationName = `Current Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
            let cityName = 'Current City';
            
            try {
              // Using OpenStreetMap Nominatim for reverse geocoding (free service)
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
              );
              
              if (response.ok) {
                const data = await response.json();
                if (data && data.display_name) {
                  locationName = data.display_name;
                  cityName = data.address?.city || data.address?.town || data.address?.village || 'Current City';
                }
              }
            } catch (geocodingError) {
              console.log('Geocoding failed, using coordinates:', geocodingError);
            }
            
            setQuery(locationName);
            setShowSuggestions(false);
            
            onLocationSelect({
              address: locationName,
              city: cityName,
              lat: lat,
              lng: lng
            });
          } catch (error) {
            console.error('Error processing current location:', error);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          setLoading(false);
          console.error('Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    }
  };

  const handleFocus = () => {
    if (query.length === 0) {
      setSuggestions(gambianLocations.slice(0, 15));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(true);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => setShowSuggestions(false), 150);
  };

  return (
    <div className="relative space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          className="pl-10 pr-16"
        />
        {query.trim() && !showSuggestions && (
          <Button
            onClick={handleManualLocationSubmit}
            disabled={loading}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-3 text-xs"
            size="sm"
            variant="outline"
          >
            {loading ? 'Validating...' : 'Use This'}
          </Button>
        )}
      </div>
      
      {showCurrentLocationButton && (
        <Button 
          variant="outline" 
          className="w-full h-12"
          onClick={getCurrentLocation}
          disabled={loading}
        >
          <Navigation className="h-5 w-5 mr-2" />
          {loading ? 'Getting Location...' : 'Use Current Location'}
        </Button>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto">
          <CardContent className="p-0">
            <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-600">
              Popular locations or type any address/Google Plus code
            </div>
            {suggestions.map((suggestion) => (
              <Button
                key={suggestion.id}
                variant="ghost"
                className="w-full justify-start h-auto p-3 border-b border-gray-100 last:border-b-0"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center gap-3 w-full">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-left">
                    <p className="font-medium">{suggestion.name}</p>
                    <p className="text-sm text-gray-600">{suggestion.address}</p>
                    <p className="text-xs text-gray-500">{suggestion.region} Region</p>
                  </div>
                </div>
              </Button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PreciseLocationSearch;
